import { motion } from "framer-motion";
import { Calendar, ArrowRight, Clock, Monitor, Ticket, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import simonImage from "@/assets/team/simon-frehner.jpg";
import carstenGrafImage from "@/assets/team/carsten-graf.jpg";

const speakerImages = [simonImage, carstenGrafImage];

const LINKEDIN_EVENT_URL = "https://www.linkedin.com/events/7452973998222708736/?viewAsMember=true";
const SIGNUP_URL = "https://intraprenoer-gmbh.webinargeek.com/mehr-produktivitaet-im-banking-durch-kuerzere-arbeitszeiten";

export function EventBanner() {
  const { t } = useLanguage();
  const e = t.event;

  return (
    <section className="py-20 bg-background">
      <div className="container px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto"
        >
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-accent/10 via-primary/5 to-accent/5 border border-accent/20 p-8 md:p-12">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent text-accent-foreground">
                  <Calendar className="h-8 w-8" />
                </div>
              </div>
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/15 text-accent text-xs font-semibold mb-3">
                  {e.badge}
                  <span className="text-foreground/50">·</span>
                  <span className="text-foreground/70">{e.languageNote}</span>
                </div>
                <h3 className="text-2xl md:text-4xl font-bold text-foreground mb-3 leading-tight">
                  {e.title}
                </h3>
                <p className="text-base md:text-lg text-foreground/70 leading-relaxed">
                  {e.lead}
                </p>
              </div>
            </div>

            {/* Hook / Context */}
            <div className="bg-background/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-foreground/5">
              <p className="text-foreground/80 leading-relaxed mb-4">
                {e.context}
              </p>
              <p className="text-foreground/80 leading-relaxed">
                {e.contextProof}
              </p>
            </div>

            {/* Speakers */}
            <div className="mb-8">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground/60 mb-4">
                {e.speakersTitle}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {e.speakers.map((s, idx) => (
                  <div
                    key={s.name}
                    className="flex items-center gap-4 bg-background/60 rounded-xl p-4 border border-foreground/5"
                  >
                    <img
                      src={speakerImages[idx]}
                      alt={s.name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-accent/30"
                    />
                    <div>
                      <div className="font-semibold text-foreground">{s.name}</div>
                      <div className="text-sm text-foreground/60 leading-snug">{s.role}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-sm text-foreground/70">
                <span className="font-medium text-foreground/80">{e.hostsLabel}: </span>
                {e.hosts.map((h, i) => (
                  <span key={h.name}>
                    <a
                      href={h.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline font-medium"
                    >
                      {h.name}
                    </a>
                    <span className="text-foreground/60"> – {h.role}</span>
                    {i < e.hosts.length - 1 && <span className="text-foreground/40"> · </span>}
                  </span>
                ))}
              </div>
            </div>

            {/* Agenda */}
            <div className="mb-8">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground/60 mb-4">
                {e.agendaTitle}
              </h4>
              <ul className="space-y-3">
                {e.agenda.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Audience + Meta */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-8 mb-8 border-b border-foreground/10">
              <p className="text-sm text-foreground/70 italic max-w-md">
                {e.audience}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-foreground/70">
                <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                  <Calendar className="h-4 w-4 text-accent" /> {e.date}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent" /> {e.duration}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-accent" /> {e.format}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-accent" /> {e.price}
                </span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
              >
                <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">
                  {e.signupButton}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full"
              >
                <a href={LINKEDIN_EVENT_URL} target="_blank" rel="noopener noreferrer">
                  {e.linkedinButton}
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
